import { ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { AuditFieldGrouping } from "@common/components/filterPopover/FilterPopover.types";
import { toTableFields } from "@common/pageView/useClientFilteredPageView";

// Mirrors Backend/Core/Filtering/Audits/AuditGroupColumns.cs. The internal assessment groups and the external audit
// groups list the same facts about a group, so both tables share one column vocabulary.
export enum AuditGroupColumn {
    Name = "name",
    Template = "questionnaireName",
    Sent = "sent",
    Deadline = "deadline",
    Responsible = "responsibleName",
    Sender = "sender",
    Recipients = "recipients",
    Opened = "opened",
    Submitted = "submitted",
    Excluded = "excluded",
    Evaluation = "evaluation",
    Progress = "progress",
}

export const allAuditGroupColumns: Array<AuditGroupColumn> = Object.values(AuditGroupColumn);

// The fields both audit group lists render.
export interface AuditGroupTableRow {
    id: string;
    name: string;
    questionnaireName: string;
    sent?: Date;
    sentString: string;
    deadline: Date;
    deadlineString: string;
    responsibleName: string;
    sender?: string;
    totalCount: number;
    openedCount: number;
    submittedCount: number;
    excludedCount: number;
    evaluatedCount: number;
    evaluationString: string;
    progress: number;
    isDraft: boolean;
}

export const auditGroupDefaultOrderBy = "deadline";
export const auditGroupDefaultOrder = "asc";

// The row property the table sorts each column on. It differs from the column id wherever the cell renders something
// other than a plain field, so this is the single place a column's sort target is defined.
const sortPropertyByColumn: Record<AuditGroupColumn, keyof AuditGroupTableRow> = {
    [AuditGroupColumn.Name]: "name",
    [AuditGroupColumn.Template]: "questionnaireName",
    [AuditGroupColumn.Sent]: "sent",
    [AuditGroupColumn.Deadline]: "deadline",
    [AuditGroupColumn.Responsible]: "responsibleName",
    [AuditGroupColumn.Sender]: "sender",
    [AuditGroupColumn.Recipients]: "totalCount",
    [AuditGroupColumn.Opened]: "openedCount",
    [AuditGroupColumn.Submitted]: "submittedCount",
    [AuditGroupColumn.Excluded]: "excludedCount",
    // The "evaluated/evaluable" text would sort "10/12" before "2/3".
    [AuditGroupColumn.Evaluation]: "evaluatedCount",
    [AuditGroupColumn.Progress]: "progress",
};

// A width is a floor, not a cap: the count columns further right are short enough to keep their own header width, so
// without these the template and responsible names are the ones squeezed into three lines.
const widthByColumn: Partial<Record<AuditGroupColumn, string>> = {
    [AuditGroupColumn.Template]: "200px",
    [AuditGroupColumn.Responsible]: "160px",
};

// Recipients are the assignees of an internal group and the legal entities of an external one, so each table names
// that column itself.
export function useAuditGroupColumns<TRow extends AuditGroupTableRow>(
    templateLabelKey: string,
    recipientsLabelKey: string,
    grouping: AuditFieldGrouping,
    renderedColumns: Array<AuditGroupColumn>
) {
    const { translateString } = useTranslation();

    const columnLabels: Record<AuditGroupColumn, string> = {
        [AuditGroupColumn.Name]: translateString("name"),
        [AuditGroupColumn.Template]: translateString(templateLabelKey),
        [AuditGroupColumn.Sent]: translateString("sent"),
        [AuditGroupColumn.Deadline]: translateString("deadline"),
        [AuditGroupColumn.Responsible]: translateString("responsible"),
        [AuditGroupColumn.Sender]: translateString("sentFrom"),
        [AuditGroupColumn.Recipients]: translateString(recipientsLabelKey),
        [AuditGroupColumn.Opened]: translateString("opened"),
        [AuditGroupColumn.Submitted]: translateString("submitted"),
        [AuditGroupColumn.Excluded]: translateString("excluded"),
        [AuditGroupColumn.Evaluation]: translateString("evaluation"),
        [AuditGroupColumn.Progress]: translateString("progress"),
    };

    const tableHeaders: Array<ITableHeader<TRow>> = renderedColumns.map((column) => ({
        property: sortPropertyByColumn[column],
        text: columnLabels[column],
        align: "left",
        showOnMobile: true,
        width: widthByColumn[column],
    }));

    return { columnLabels, tableFields: toTableFields(allAuditGroupColumns, columnLabels, grouping), tableHeaders };
}

// The fields of either group list's API row that the shared cells read their display values from.
interface AuditGroupApiRow {
    questionnaire: { name: string };
    responsible: { name: string };
    sent?: Date;
    deadline: Date;
    evaluatedCount: number;
    evaluableCount: number;
}

export function toAuditGroupTableRows<TRow extends AuditGroupApiRow>(rows: Array<TRow>, translateDate: (date: Date) => string) {
    return rows.map((row) => ({
        ...row,
        questionnaireName: row.questionnaire.name,
        responsibleName: row.responsible.name,
        sentString: row.sent ? translateDate(row.sent) : "",
        deadlineString: translateDate(row.deadline),
        evaluationString: `${row.evaluatedCount}/${row.evaluableCount}`,
    }));
}
